import React from 'react';
import { useOptionalTheme } from '@shared/providers/theme/ThemeContext';
import { useOptionalAdminTheme } from '@shared/providers/admin-theme/AdminThemeContext';
import { useBottomBar } from '@shared/providers/BottomBarContext';
import { useSidebarOpen } from '@shared/providers/SidebarContext';

const BOTTOM_BAR_Z_INDEX = 1060;

export interface BottomBarProps {
  // Footer props
  siteName?: string;
  displayName?: string;
  backgroundColor?: string;
  useAdmin?: boolean;

  // Shared props
  variant?: 'footer' | 'action';
  visible?: boolean;
  left?: React.ReactNode;
  right?: React.ReactNode;
  children?: React.ReactNode; // alternative custom content
  className?: string;
  style?: React.CSSProperties;
  fixed?: boolean; // fixed to bottom
}

const BottomBar: React.FC<BottomBarProps> = ({
  siteName,
  displayName,
  useAdmin,
  variant = 'footer',
  visible = true,
  left,
  right,
  children,
  className = '',
  style,
  fixed = true,
}) => {
  // Always get sidebar state to adjust fixed positioning
  const sidebarOpen = useSidebarOpen();

  // If admin theme is requested, prefer that; otherwise prefer tenant theme.
  //
  // Both contexts are read on every render — `useAdminTheme` / `useTheme`
  // throw outside their provider, which is why this used to pick between them
  // with a conditional call; a hook called on some renders and not others is
  // the one thing React does not allow. The optional readers return null
  // instead of throwing, so the choice moves out of the hook call and into the
  // value.
  const optionalAdminTheme = useOptionalAdminTheme();
  const optionalTenantTheme = useOptionalTheme();
  const adminTheme = useAdmin ? optionalAdminTheme : null;
  const tenantTheme = useAdmin ? null : optionalTenantTheme;

  // When an action BottomBar is visible and fixed, signal to the global provider
  // so the global Footer can avoid rendering.
  const { setHasActionBar } = useBottomBar();
  React.useEffect(() => {
    if (variant === 'action' && fixed && visible) {
      setHasActionBar(true);
      return () => setHasActionBar(false);
    }
    return;
  }, [variant, fixed, visible, setHasActionBar]);

  if (!visible) return null;

  const bg = adminTheme?.colors?.primaryDark || tenantTheme?.colors?.primaryDark || '#333';
  const name =
    displayName ||
    siteName ||
    adminTheme?.themeConfig?.siteName ||
    tenantTheme?.theme?.displayName ||
    '';

  const containerStyle: React.CSSProperties = {
    backgroundColor: bg,
    ...(fixed
      ? {
          position: 'fixed',
          bottom: 0,
          // The bar is viewport-fixed, so its left edge has to meet the right
          // edge of whatever sidebar is on screen. 240px is the console's; a
          // tenant that replaces the chrome (a custom shell may use a wider
          // rail) sets `--app-sidebar-width` and the bar follows it instead of
          // leaving a strip of content uncovered beside it.
          left: sidebarOpen ? 'var(--app-sidebar-width, 240px)' : '0px',
          right: 0,
          transition: 'left 0.3s ease',
          // AntD Select/Dropdown popup z-index is 1050 by default.
          // Keep the fixed bottom bar above opened option menus.
          zIndex: BOTTOM_BAR_Z_INDEX,
        }
      : {}),
    ...style,
  };

  return (
    <div
      className={`bottom-bar px-6 py-3 border-t border-gray-200 shadow-lg ${className}`}
      style={containerStyle}
      role="region"
      aria-label="bottom-bar"
      data-variant={variant}
    >
      <div className="flex items-center justify-between mx-auto">
        {variant === 'footer' ? (
          <div className="w-full text-center">
            <p className="text-sm text-white">
              © {new Date().getFullYear()} {name}. All rights reserved.
            </p>
          </div>
        ) : (
          <>
            <div className="flex gap-2">{left}</div>
            <div className="flex gap-3">{children ?? right}</div>
          </>
        )}
      </div>
    </div>
  );
};

BottomBar.displayName = 'BottomBar';

// Maintain a Footer wrapper for backward compatibility
export const Footer: React.FC<{
  siteName?: string;
  displayName?: string;
  backgroundColor?: string;
  useAdmin?: boolean;
  fixed?: boolean;
}> = ({ siteName, displayName, backgroundColor, useAdmin, fixed = true }) => {
  // Render the footer only when there is no active action BottomBar
  const { hasActionBar } = useBottomBar();

  // Footer should not render if an action bar is active
  if (hasActionBar) {
    return null;
  }

  return (
    <BottomBar
      variant="footer"
      siteName={siteName}
      displayName={displayName}
      backgroundColor={backgroundColor}
      useAdmin={useAdmin}
      fixed={fixed}
    />
  );
};

export default BottomBar;
